import {
  ActionButton,
  ActionLink,
  Appear,
  BodyText,
  Caption,
  Icon,
  IconTile,
  PageContainer,
  SectionTitle,
  StatCard,
  StrongValue,
  SubsectionTitle,
  Subtitle,
  TooltipIconButton,
  TrendCaption,
  useCan,
  useIfUser,
  usePreferenceEnabled,
} from '@if/mui'
import { Box, Card, Skeleton, Stack, styled } from '@mui/material'
import { access } from '@src/access'
import { AddApplicationButton } from '@src/components/AddApplication'
import { NavigationCard } from '@src/components/NavigationCard'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useInvitationDialog } from '@src/containers/BrowseCreditApplicationPage/components/InvitationDialog'
import { useResourcesDialog } from '@src/containers/App/components/ResourcesDialog'
import { useBorrowerContext } from '@src/hooks/borrower-context-hooks'
import { useCanInviteApplicant } from '@src/hooks/invitation-hooks'
import { useUnreadEntries } from '@src/data/api/chat-message/chat-message-api'
import { useGetHomeKpi } from '@src/data/api/merchants-api/merchants-api'
import { useNewApplicationPrograms } from '@src/data/merchant-config-selector'

import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Calculator,
  ChartColumn,
  CheckCircle,
  Clock,
  LayoutDashboard,
  MessageSquare,
  Plus,
  Settings2,
  ShieldCheck,
  TriangleAlert,
  UserPlus,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type Severity = 'success' | 'warning' | 'info'

// Layout-only styled surfaces (no theme overrides): the main two-column split
// and the New Application hero panel.
const StatsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('md')]: { gridTemplateColumns: 'repeat(3, 1fr)' },
}))

// Every navigation card is exactly one row tall and the hero spans two of them, so
// the block always bottoms out on one line whatever the card count — the mockup's
// "fixed height, flexible width" rule. This is the one number to tune: the hero
// follows from it as `HERO_ROWS * NAV_CARD_HEIGHT + one gap`.
const NAV_CARD_HEIGHT = '13.5rem'
/** Rows the hero spans, and with it the depth of the card block beside it. */
const HERO_ROWS = 2
/** Columns left free beside the hero, from `lg` up. Below that it takes a whole row. */
const COLUMNS_BESIDE_HERO = 2
/** Cells beside the hero, filled before the full-width tail rows begin. */
const BLOCK_SLOTS = COLUMNS_BESIDE_HERO * HERO_ROWS
/** Entrance delay step between consecutive cards, in ms — `Stagger`'s default. */
const STAGGER_STEP = 70
/** `localStorage` flag behind the Protection Plan banner — absent reads as shown. */
const PROTECTION_PLAN_VISIBLE_KEY = 'home-protection-plan-visible'

const HomeGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  gridTemplateColumns: '1fr',
  // Rows are fixed at the card height; `auto` only ever kicks in for the hero's own
  // row below `lg`, where it sits full-width on a row of its own. The cards clamp
  // their text (see `NavigationCard`), so they never ask for more than the minimum.
  gridAutoRows: `minmax(${NAV_CARD_HEIGHT}, auto)`,
  [theme.breakpoints.up('md')]: { gridTemplateColumns: 'repeat(2, 1fr)' },
  // The mockup's 5fr / 7fr split, with the 7fr half as the two card columns.
  [theme.breakpoints.up('lg')]: { gridTemplateColumns: '5fr 3.5fr 3.5fr' },
  [theme.breakpoints.up('xl')]: { gridTemplateColumns: 'repeat(3, 1fr)' },
}))

/**
 * Columns each card spans so the last — possibly partly filled — row always covers
 * the full grid width. Grid never stretches a short final row on its own, but the
 * card count is known at render and so is the column count per breakpoint, so the
 * spans are plain arithmetic: no measuring, no resize observer.
 *
 * `besideHero` is how many columns are free next to the hero (0 when the hero takes
 * a row of its own). Leftover columns go to the trailing cards, widest last, which
 * is how the mockup lays out its two-card bottom row.
 */
const columnSpans = (total: number, columns: number, besideHero: number): number[] => {
  const spans = Array<number>(total).fill(1)
  if (total < 1 || columns < 2) return spans

  const tail = Math.max(0, total - besideHero * HERO_ROWS)
  const rowWidth = tail > 0 ? columns : besideHero
  const inLastRow = (((tail > 0 ? tail : total) - 1) % rowWidth) + 1

  for (let i = 0; i < rowWidth - inLastRow; i++) spans[total - 1 - (i % inLastRow)] += 1
  return spans
}

// Shared surface for the hero and the Protection Plan banner below: splash-blue tint,
// splash-blue border, larger corner radius. Base for `styled()` only — rendered
// directly, its `sx` prop is ignored.
const HighlightPanel = styled(Card)(({ theme }) => ({
  backgroundColor: theme.vars.palette.secondary.background,
  border: `2px solid ${theme.vars.palette.secondary.border}`,
  borderRadius: Number(theme.shape.borderRadius) * 2, // ~2x the DS default radius — matches the mockup panel
  // Dark: a deep-navy surface with a splash "baby blue" border.
  ...theme.applyStyles('dark', {
    backgroundColor: theme.vars.palette.primary.background,
    borderColor: theme.vars.palette.primary.main,
  }),
}))

// The New Application hero: the highlight surface as a tall column. `overflow` goes
// back to visible because the grid fixes the panel's height and Card would otherwise
// clip the longer FR copy.
const HeroPanel = styled(HighlightPanel)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(4),
  overflow: 'visible',
}))

// The Protection Plan banner: the same highlight surface on one full-width row, the
// icon tile beside the title and its deck. Nothing fixes its height, so the copy
// sets the depth and neither language can be clipped.
//
// The deck is the only child that grows: it wraps to 4-5 lines on a phone while the
// tile and the dismiss button keep their fixed square. Centring them would drop both
// down the paragraph — beside its middle rather than the title — so they hang from
// the top edge instead and stay level with the heading at every width.
const ProtectionPlanPanel = styled(HighlightPanel)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(3),
}))

// The hero's headline icon — a solid brand tile (navy in light, splash in dark)
// with a white glyph, matching the mockup's `bg-primary text-white` hero icon (a
// filled tile, unlike the soft IconTile the nav cards use).
const HeroIcon = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.vars.palette.primary.main,
  color: theme.vars.palette.primary.contrastText,
  boxShadow: theme.shadows[2],
}))

// The icon + `highlight` share the severity color; the trailing text keeps the
// Caption's muted color.
const StatCaption = ({
  icon,
  color,
  highlight,
  rest,
}: {
  icon: LucideIcon
  color: Severity
  highlight: ReactNode
  rest: ReactNode
}) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Icon icon={icon} size="sm" color={color} />
    <Caption>
      <Box component="span" sx={{ color: `${color}.main` }}>
        {highlight}
      </Box>{' '}
      {rest}
    </Caption>
  </Stack>
)

const HomePage = () => {
  const { t } = useTranslation()
  const ifUser = useIfUser()
  const merchant = useMerchant()
  const context = useBorrowerContext()
  const invite = useInvitationDialog()
  const canCreateApplications = useCan(access.createApplication)
  const canInviteApplicant = useCanInviteApplicant()
  const [unreadEntries] = useUnreadEntries()
  const unreadMessages = unreadEntries.length

  const canUseCalculator = useCan(access.loanCalculator, { merchant })
  const canUseCustomBuydownBuilder = useCan(access.customBuydownBuilder, { merchant })
  const canUseInsights = useCan(access.merchantStatistics, { merchant })
  const canUseProtectionPlan = useCan(access.protectionPlan, { merchant })

  const [kpi, isKpiLoading] = useGetHomeKpi()
  const [showProtectionPlan, setShowProtectionPlan] = usePreferenceEnabled(PROTECTION_PLAN_VISIBLE_KEY)

  const programs = useNewApplicationPrograms(merchant)
  const resourcesDialog = useResourcesDialog(merchant)
  // The hero is the New Application panel: with neither action available it would be an
  // empty prompt, so it goes away entirely and the cards take back its column.
  const showHero = canCreateApplications || canInviteApplicant

  const sectionCards: ReactElement[] = [
    <NavigationCard
      key="applications"
      to="/credits"
      icon={LayoutDashboard}
      iconColor="info"
      title={t('nav.applications')}
      description={t('home.cards.applications')}
    />,
    ...(canUseCalculator
      ? [
          <NavigationCard
            key="calculator"
            to="/loan-calculator"
            icon={Calculator}
            iconColor="info"
            title={t('nav.calculator')}
            description={t('home.cards.calculator')}
          />,
        ]
      : []),
    ...(canUseCustomBuydownBuilder
      ? [
          <NavigationCard
            key="planBuilder"
            to="/custom-buydown"
            icon={Settings2}
            iconColor="info"
            title={t('nav.paymentPlanBuilder')}
            description={t('home.cards.planBuilder', { context })}
          />,
        ]
      : []),
    ...(canUseInsights
      ? [
          <NavigationCard
            key="insights"
            to="/merchant-statistics"
            icon={BarChart3}
            iconColor="info"
            title={t('nav.insights')}
            description={t('home.cards.insights')}
          />,
        ]
      : []),
    ...(resourcesDialog.resources.length > 0
      ? [
          <NavigationCard
            key="resources"
            onClick={() => void resourcesDialog.open()}
            icon={BookOpen}
            iconColor="info"
            title={t('nav.resources')}
            description={t('home.cards.resources')}
          />,
        ]
      : []),
  ]

  // Messaging, matching the sidebar nav item. It follows the cards that fill the block beside the hero so it lands in
  // the first cell of the tail row, directly under the hero, as the mockup has it.
  const messagingCards: ReactElement[] = [
    <NavigationCard
      key="messages"
      to="/chat-dashboard"
      icon={MessageSquare}
      title={t('home.messages.title')}
      showArrow={false}
      description={
        <Caption>
          <StrongValue color="error.main">{unreadMessages}</StrongValue> {t('home.messages.unread')}
        </Caption>
      }
    />,
  ]

  const cards: ReactElement[] = [
    ...sectionCards.slice(0, BLOCK_SLOTS),
    ...messagingCards,
    ...sectionCards.slice(BLOCK_SLOTS),
  ]

  // Below `md` there is a single column, so nothing ever needs to stretch.
  const spansMd = columnSpans(cards.length, 2, 0)
  const spansLg = columnSpans(cards.length, 3, showHero ? COLUMNS_BESIDE_HERO : 0)

  const heroFeatures: { icon: LucideIcon; label: string }[] = [
    { icon: Zap, label: t('home.hero.features.prequalification') },
    { icon: CheckCircle, label: t('home.hero.features.planOptions') },
    { icon: Clock, label: t('home.hero.features.instantDecisions') },
  ]

  return (
    <PageContainer
      overline={merchant.name}
      title={t('home.welcome', { name: ifUser.firstName })}
      subtitle={t('home.subtitle')}
    >
      <Stack spacing={4}>
        {/* Quick stats */}
        {isKpiLoading || !kpi ? (
          <StatsGrid>
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} variant="rounded" height={132} />
            ))}
          </StatsGrid>
        ) : (
          <StatsGrid>
            <StatCard
              icon={LayoutDashboard}
              iconColor="success"
              label={t('home.stats.activeApplications')}
              value={kpi.activeApplications}
              caption={<TrendCaption deltaPercent={kpi.applicationsTrendPercent} />}
            />
            <StatCard
              icon={Clock}
              iconColor="warning"
              label={t('home.stats.pendingReview')}
              value={kpi.pendingReview}
              caption={
                <StatCaption
                  icon={TriangleAlert}
                  color="warning"
                  highlight={kpi.requiredActionMerchant}
                  rest={t('home.stats.requiredAction')}
                />
              }
            />
            <StatCard
              icon={ChartColumn}
              iconColor="info"
              label={t('home.stats.completedLast30Days')}
              value={kpi.completedLast30Days}
              caption={<TrendCaption deltaPercent={kpi.completedTrendPercent} />}
            />
          </StatsGrid>
        )}

        {canUseProtectionPlan &&
          (showProtectionPlan ? (
            <ProtectionPlanPanel>
              <IconTile icon={ShieldCheck} color="info" />
              <Stack spacing={0.5} flex={1}>
                <SubsectionTitle>{t('home.protectionPlan.title')}</SubsectionTitle>
                <Caption>{t('home.protectionPlan.subtitle')}</Caption>
              </Stack>
              <ActionButton
                emphasis="principal"
                size="large"
                href="/protection-plan"
                sx={{ flexShrink: 0, alignSelf: 'center' }}
              >
                {t('home.protectionPlan.viewOpportunities')}
              </ActionButton>
              <Box sx={{ alignSelf: 'center', display: 'flex' }}>
                <TooltipIconButton
                  title={t('home.protectionPlan.dismiss')}
                  onClick={() => setShowProtectionPlan(false)}
                >
                  <Icon icon={X} />
                </TooltipIconButton>
              </Box>
            </ProtectionPlanPanel>
          ) : (
            <Box sx={{ alignSelf: 'flex-start' }}>
              <ActionLink onClick={() => setShowProtectionPlan(true)}>
                <Icon icon={ShieldCheck} size="sm" />
                {t('home.protectionPlan.show')}
              </ActionLink>
            </Box>
          ))}

        {/* Main content: the hero and every navigation card share one grid, so the
            hero's two rows and the cards beside it bottom out on the same line. */}
        <HomeGrid>
          {/* New Application hero */}
          {showHero && (
            <HeroPanel sx={{ gridColumn: { xs: '1 / -1', lg: '1' }, gridRow: { lg: `1 / span ${HERO_ROWS}` } }}>
              <Stack spacing={3} flex={1}>
                <HeroIcon>
                  <Icon icon={Plus} size="lg" />
                </HeroIcon>
                <Stack spacing={0.5}>
                  <SectionTitle>{t('home.hero.title')}</SectionTitle>
                  <Subtitle>{t('home.hero.subtitle', { context })}</Subtitle>
                </Stack>
                <Stack spacing={2}>
                  {heroFeatures.map(({ icon, label }) => (
                    <Stack key={label} direction="row" spacing={1.5} alignItems="center">
                      <IconTile icon={icon} size="sm" />
                      <BodyText>{label}</BodyText>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} pt={3}>
                {canCreateApplications && (
                  <AddApplicationButton
                    size="large"
                    startIcon={undefined}
                    endIcon={ArrowRight}
                    supportedFinancingPrograms={programs}
                    sx={{ flex: 1 }}
                  >
                    {t('home.hero.start')}
                  </AddApplicationButton>
                )}
                {canInviteApplicant && (
                  <ActionButton
                    startIcon={UserPlus}
                    size="large"
                    onClick={() =>
                      void invite.open({
                        financingCompanyId: merchant.financingCompanyId,
                        loanPurposeId: merchant.serviceCategory ?? null,
                      })
                    }
                    sx={{ flex: 1 }}
                  >
                    {t('nav.inviteApplicant')}
                  </ActionButton>
                )}
              </Stack>
            </HeroPanel>
          )}

          {/* Navigation cards. `Appear` is the grid item, so the span goes on it —
              `Stagger` clones its children without props and can't carry it. */}
          {cards.map((card, index) => (
            <Appear
              key={card.key}
              delay={index * STAGGER_STEP}
              sx={{ gridColumn: { md: `span ${spansMd[index]}`, lg: `span ${spansLg[index]}` } }}
            >
              {card}
            </Appear>
          ))}
        </HomeGrid>
      </Stack>
    </PageContainer>
  )
}

export default HomePage
